import { useMemo } from 'react';
import { useQuery } from 'react-query';
import useToast from '@/hooks/use-toast.hook';
import useFormHandle from '@/hooks/use-form-handle.hook';
import { PolicyModelV1 } from '@/lib/models/policy-model-v1/policy-v1.model';
import FormSection from '@/components/form/form-section';
import InputControl from '@/components/form/input-control';
import FormButtons from '@/components/form/form-buttons';
import useQueryHelper from '@/hooks/use-query-helper';
import { getOrgPolicyV1, patchPolicyV1 } from '@/lib/services/policy-v1.service';
import { QueryKey } from '@/lib/query-client';
import { Form } from '@/components/form';
import useFeature from '@/hooks/use-feature.hook';
import PageFeatureToast from '@/components/page-feature-toast';
import { getAllAgents } from '@/lib/services/agent.service';
import { Box, Grid } from '@mui/material';
import FormLabel from '@/components/form-label';
import InlineToast from '@/components/toasts/inline';
import ModelOption from '@/components/model-option';
import { getAllPrivateModels } from '@/lib/services/private-model.service';
import { createUseStyles } from 'react-jss';
import { FieldError } from '@/lib/services';
import AgentLink from '@/components/agent-link';
import FormSectionSubtitle from '@/components/form/form-section/form-section-subtitle';

const fields = ['contextDataSources', 'assistants', 'portal'];
const formModel = new PolicyModelV1();

const useStyles = createUseStyles({
  portalSelect: {
    '& .MuiFormControl-root': {
      marginTop: 0,
    },
  },
});

const PortalForm = () => {
  const { errorToast, toast } = useToast();
  const { canChangePolicy, canChangeAgents } = useFeature();
  const styles = useStyles();

  const formHandle = useFormHandle({
    initialValues: formModel,
    validationSchema: formModel.schemaSlice(fields),
    onSubmit: async (values, { setSubmitting, resetForm, setFieldError }) => {
      const setFieldErrors = (fieldErrors: FieldError[]) => {
        fieldErrors.forEach(({ msg, path }) => setFieldError(path, msg));
      };

      const { patched, fieldErrors, error } = await patchPolicyV1(
        values.id,
        values.propSlice(fields)
      );
      setSubmitting(false);

      if (patched) {
        resetForm({ values });
        toast('The default policy was updated');
        return;
      }

      if (fieldErrors) {
        setFieldErrors(fieldErrors);
        return;
      }

      errorToast(error || 'The default policy was not updated');
    },
  });

  const { values } = formHandle;

  const orgPolicyQuery = useQuery([QueryKey.OrgPolicyView], async () => getOrgPolicyV1());
  const { showLoader: policyLoader } = useQueryHelper(orgPolicyQuery);

  const agentsQuery = useQuery([QueryKey.AgentsList], async () => getAllAgents());
  const { data: agents } = agentsQuery;
  const { showLoader: agentsLoading } = useQueryHelper(agentsQuery);

  const agentOptions = useMemo(() => {
    return (agents || []).map((agent) => ({
      value: agent.id,
      label: <AgentLink agent={agent} showAdminName />,
    }));
  }, [agents]);

  const privateModelsQuery = useQuery([QueryKey.PrivateModelsList], async () =>
    getAllPrivateModels()
  );

  const { data: privateModels } = privateModelsQuery;
  const { showLoader: privateModelsLoading } = useQueryHelper(privateModelsQuery);

  const availablePrivateModelOptions = useMemo(() => {
    return (privateModels || []).map(({ name, id, modelId }) => {
      const isDefault = id === values.portal.defaultModelId;

      return {
        label: <ModelOption name={name} modelId={modelId} />,
        value: id,
        disabled: isDefault,
        tooltip: isDefault ? 'The default model must be enabled' : '',
      };
    });
  }, [privateModels, values.portal.defaultModelId]);

  const defaultPrivateModelOptions = useMemo(() => {
    return (privateModels || []).map(({ name, id, modelId }) => ({
      label: <ModelOption name={name} modelId={modelId} />,
      value: id,
      disabled: !values.portal.availablePrivateModelIds.includes(id),
    }));
  }, [privateModels, values.portal.availablePrivateModelIds]);

  if (policyLoader || agentsLoading) {
    return;
  }

  const hasPrivateModel = !!availablePrivateModelOptions.length;

  return (
    <Form formHandle={formHandle} query={orgPolicyQuery}>
      <PageFeatureToast featureId="change-policy" can={canChangePolicy} />

      <FormSection title="Allow Access to Portal" scrollId="portal-access">
        <FormSectionSubtitle>
          Enable or disable Portal access for your entire organization.
        </FormSectionSubtitle>
        <Grid container spacing={2}>
          <Grid item xs={5}>
            <InputControl
              label="Portal Enabled"
              name="portal.enabled"
              type="switch"
              formHandle={formHandle}
              readonly={!canChangePolicy}
            />
          </Grid>
        </Grid>
      </FormSection>

      <FormSection title="Portal Private Model Settings" scrollId="portal-private-model-settings">
        <FormSectionSubtitle>
          Choose which private models are available, and the default, when a user is redirected to
          the Portal.
        </FormSectionSubtitle>
        <Grid container columnSpacing={8} rowSpacing={4}>
          <Grid item xs={6}>
            <Box>
              <FormLabel>Enabled Private Models</FormLabel>
              <InputControl
                name="portal.availablePrivateModelIds"
                label=""
                type="switch-list"
                options={availablePrivateModelOptions}
                formHandle={formHandle}
                disabled={!hasPrivateModel}
                readonly={!canChangePolicy}
                emptyMessage="No private models available"
              />
            </Box>
          </Grid>

          <Grid item xs={6}>
            <FormLabel>Default Private Model</FormLabel>
            <Box className={styles.portalSelect}>
              <InputControl
                label=""
                name="portal.defaultModelId"
                type="select"
                disabled={!hasPrivateModel}
                options={defaultPrivateModelOptions}
                formHandle={formHandle}
                readonly={!canChangePolicy}
              />
            </Box>

            {!privateModelsLoading && !hasPrivateModel && (
              <Box mt={2.5}>
                <InlineToast
                  level="warning"
                  message="Enable a Private Model to enable this feature"
                />
              </Box>
            )}
          </Grid>
        </Grid>
      </FormSection>

      <FormSection title="Data Source Assignment" scrollId="data-source-assignment">
        <InputControl
          name="contextDataSources.enabled"
          formHandle={formHandle}
          readonly={!canChangePolicy}
          type="datasources-list-toggle"
          leftTitle="Available Data Sources"
          rightTitle="Assigned Data Sources"
        />
      </FormSection>

      <FormSection title="Assistant Assignment" scrollId="assistant-assignment">
        <PageFeatureToast featureId="change-agent" can={canChangeAgents} />
        <InputControl
          name="assistants.enabled"
          label="Assistants"
          formHandle={formHandle}
          readonly={!canChangeAgents}
          type="list-toggle"
          leftTitle="Available Assistants"
          rightTitle="Assigned Assistants"
          options={agentOptions}
        />
      </FormSection>

      <FormButtons formHandle={formHandle} readonly={!canChangePolicy} />
    </Form>
  );
};

export default PortalForm;
